import {useField} from 'formik';
import React, {ReactElement, useContext, useMemo} from 'react';
import {ACCEPTED_AUDIO_FILE_TYPES, ACCEPTED_FILE_AND_DOCUMENT_FILE_TYPES, ACCEPTED_VIDEO_FILE_TYPES} from '../../../../../../constants/file-uploads';
import {ConfiguredField} from '../../../../../../shared/model/field.model';
import {NxFile} from '../../../../../../shared/model/nx-file.model';
import NxFileUpload from '../../../../../shared/inputs/nx-file-upload/NxFileUpload';
import {CustomFieldValue} from '../../customer-data/custom-fields/custom-field.model';
import {CheckValue} from '../custom-checks/check-value.model';
import {StepContext} from '../loan-application-step/LoanApplicationStep';
import {FieldDisablementReason} from './field-disablement-reason.model';
import {ConfiguredFieldWrapper} from './ConfiguredFieldWrapper';
import styles from './ConfiguredFieldFileInput.module.scss';
import {AcceptedFileProperties, MaximumFileSize} from '../../../../../shared/inputs/nx-file-upload/file-upload.model';

interface ConfiguredFieldError {
  value?: {
    file: string;
    isChanged: boolean;
  };
  enabled?: string;
  required?: string;
  name?: string;
}

interface Props {
  label: React.ReactNode;
  name: string;
  configuredField: ConfiguredField<NxFile>;
  disablementReason?: FieldDisablementReason;
}

type CustomFileFieldValue = CustomFieldValue<NxFile> | CheckValue<NxFile>;

const acceptedFileProperties: AcceptedFileProperties[] = [
  {
    types: ACCEPTED_FILE_AND_DOCUMENT_FILE_TYPES,
    maxSize: MaximumFileSize.DEFAULT
  },
  {
    types: [...ACCEPTED_AUDIO_FILE_TYPES, ...ACCEPTED_VIDEO_FILE_TYPES],
    maxSize: MaximumFileSize.AUDIO_VIDEO
  }
];

export const ConfiguredFieldFileInput = ({label, name, configuredField, disablementReason}: Props): ReactElement => {

  const {isStepReadonly} = useContext(StepContext);
  const {enabled, required} = configuredField;
  const [field, meta, helper] = useField<CustomFileFieldValue>(name);
  const nxFile = useMemo(() => field.value?.value, [field.value]);

  const handleChange = (file?: File): Promise<void> | void => {

    const newValue = {
      ...field.value,
      value: {file, isChanged: true}
    };

    helper.setValue(newValue);
  };

  const externalError = (meta.error as ConfiguredFieldError)?.value?.file;

  return (
    <ConfiguredFieldWrapper enabled={enabled} disablementReason={disablementReason} className={styles.fileUploadWrapper}>
      <NxFileUpload disabled={isStepReadonly || !!disablementReason}
                    acceptedFileProperties={acceptedFileProperties}
                    className={styles.fileUpload}
                    externalError={externalError}
                    onBlur={(): void => helper.setTouched(true)}
                    onChange={handleChange}
                    value={nxFile?.file}
                    required={required}
                    label={label} />
    </ConfiguredFieldWrapper>
  );
};
